import { gql } from '@urql/core';

export const GET_CHANNEL_OPS_MODE = gql`
  query GetChannelOpsMode {
    activeChannel {
      id
      customFields {
        operationsMode
        opsPickingRequired
        opsPrepTimeMinutes
        opsPickingMode
      }
    }
  }
`;

export const GET_CHANNEL_OPS_SETTINGS = gql`
  query GetChannelOpsSettings {
    activeChannel {
      id
      customFields {
        operationsMode
        opsPickingRequired
        opsPackingRequired
        opsAutoAssign
        opsPrepTimeMinutes
        opsPickingMode
        opsDeliveryRequired
      }
    }
  }
`;

export const UPDATE_OPS_SETTINGS = gql`
  mutation UpdateOpsSettings($input: UpdateChannelInput!) {
    updateChannel(input: $input) {
      ... on Channel {
        id
        customFields {
          operationsMode
          opsPickingRequired
          opsPackingRequired
          opsAutoAssign
          opsPrepTimeMinutes
        }
      }
      ... on LanguageNotAvailableError {
        errorCode
        message
      }
    }
  }
`;

export const GET_OPERATIONS_BOARD = gql`
  query GetOperationsBoard($options: OrderListOptions) {
    orders(options: $options) {
      items {
        id
        code
        state
        total
        totalWithTax
        currencyCode
        orderPlacedAt
        createdAt
        customer {
          id
          firstName
          lastName
          emailAddress
        }
        shippingAddress {
          fullName
          city
          country
        }
        lines {
          id
          quantity
          productVariant {
            id
            name
            sku
            featuredAsset {
              preview
            }
          }
        }
        payments {
          id
          method
          state
          amount
        }
        fulfillments {
          id
          state
          createdAt
          lines {
            orderLineId
            quantity
          }
          customFields {
            deliveryType
            pickupLocationName
            readyForPickupAt
          }
        }
        customFields {
          scheduledDate
          scheduledSlotStart
          scheduledSlotLabel
        }
      }
      totalItems
    }
  }
`;

export const GET_ACTIVE_OPS_TASKS = gql`
  query GetActiveOpsTasks {
    pickLists(options: { take: 200, filter: { state: { notIn: ["completed", "cancelled"] } } }) {
      items {
        id
        fulfillmentId
        assignedToId
        state
        batchId
      }
    }
    packTasks(options: { take: 200, filter: { state: { notIn: ["completed", "cancelled"] } } }) {
      items {
        id
        fulfillmentId
        assignedToId
        state
      }
    }
    deliveryTasks(options: { take: 200, filter: { state: { notIn: ["delivered", "cancelled"] } } }) {
      items {
        id
        fulfillmentId
        assignedToId
        state
      }
    }
  }
`;

export const GET_PICK_LIST = gql`
  query GetPickList($id: ID!) {
    pickList(id: $id) {
      id
      createdAt
      channelId
      fulfillmentId
      orderId
      assignedToId
      state
      totalItems
      pickedItems
      unavailableItems
      assignedAt
      startedAt
      completedAt
      items {
        id
        orderLineId
        productVariantId
        productName
        sku
        barcode
        shelfLocation
        imageUrl
        expectedQuantity
        pickedQuantity
        sortOrder
        state
        unavailableReason
        substituteVariantId
        substituteProductName
        substituteSku
        substituteBarcode
        substituteImageUrl
        substituteQuantity
      }
    }
  }
`;

export const GET_AVAILABLE_PICKERS = gql`
  query GetAvailablePickers {
    availablePickers {
      id
      firstName
      lastName
      emailAddress
    }
  }
`;

export const ASSIGN_PICK_LIST_MUTATION = gql`
  mutation AssignPickList($pickListId: ID!, $pickerId: ID!) {
    assignPickList(pickListId: $pickListId, pickerId: $pickerId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const START_PICKING_MUTATION = gql`
  mutation StartPicking($pickListId: ID!) {
    startPicking(pickListId: $pickListId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const PICK_ITEM_MUTATION = gql`
  mutation PickItem($pickListItemId: ID!, $pickedQuantity: Int!) {
    pickItem(pickListItemId: $pickListItemId, pickedQuantity: $pickedQuantity) {
      id
      pickedQuantity
      state
    }
  }
`;

export const MARK_ITEM_UNAVAILABLE_MUTATION = gql`
  mutation MarkItemUnavailable($pickListItemId: ID!, $reason: String) {
    markItemUnavailable(pickListItemId: $pickListItemId, reason: $reason) {
      id
      pickedQuantity
      state
    }
  }
`;

export const SUBSTITUTE_ITEM_MUTATION = gql`
  mutation SubstituteItem($pickListItemId: ID!, $substituteVariantId: ID!, $substituteQuantity: Int!) {
    substituteItem(
      pickListItemId: $pickListItemId
      substituteVariantId: $substituteVariantId
      substituteQuantity: $substituteQuantity
    ) {
      id
      pickedQuantity
      state
    }
  }
`;

export const COMPLETE_PICKING_MUTATION = gql`
  mutation CompletePicking($pickListId: ID!) {
    completePicking(pickListId: $pickListId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const CREATE_BATCH_MUTATION = gql`
  mutation CreateBatch($pickListIds: [ID!]!, $pickerId: ID!) {
    createBatch(pickListIds: $pickListIds, pickerId: $pickerId) {
      batchId
      success
      errorCode
      message
    }
  }
`;

export const GET_BATCH_CONSOLIDATED_ITEMS = gql`
  query GetBatchConsolidatedItems($batchId: String!) {
    batchConsolidatedItems(batchId: $batchId) {
      productVariantId
      productName
      sku
      barcode
      shelfLocation
      imageUrl
      totalQuantity
      pickedQuantity
      orderCount
      items {
        pickListItemId
        pickListId
        orderId
        expectedQuantity
        pickedQuantity
        state
      }
    }
  }
`;

export const START_BATCH_PICKING_MUTATION = gql`
  mutation StartBatchPicking($batchId: String!) {
    startBatchPicking(batchId: $batchId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const PICK_BATCH_ITEM_MUTATION = gql`
  mutation PickBatchItem($batchId: String!, $productVariantId: Int!, $totalPickedQuantity: Int!) {
    pickBatchItem(
      batchId: $batchId
      productVariantId: $productVariantId
      totalPickedQuantity: $totalPickedQuantity
    ) {
      id
      pickedQuantity
      state
    }
  }
`;

export const COMPLETE_BATCH_PICKING_MUTATION = gql`
  mutation CompleteBatchPicking($batchId: String!) {
    completeBatchPicking(batchId: $batchId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const TRANSITION_OPS_FULFILLMENT = gql`
  mutation TransitionOpsFulfillment($fulfillmentId: ID!, $state: String!) {
    transitionOpsFulfillment(fulfillmentId: $fulfillmentId, state: $state) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const GET_AVAILABLE_PACKERS = gql`
  query GetAvailablePackers {
    availablePackers {
      id
      firstName
      lastName
      emailAddress
    }
  }
`;

export const ASSIGN_PACK_TASK_MUTATION = gql`
  mutation AssignPackTask($packTaskId: ID!, $packerId: ID!) {
    assignPackTask(packTaskId: $packTaskId, packerId: $packerId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const START_PACK_TASK_MUTATION = gql`
  mutation StartPackTask($packTaskId: ID!) {
    startPackTask(packTaskId: $packTaskId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const COMPLETE_PACK_TASK_MUTATION = gql`
  mutation CompletePackTask($packTaskId: ID!, $notes: String) {
    completePackTask(packTaskId: $packTaskId, notes: $notes) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const GET_PACK_TASK_BY_FULFILLMENT = gql`
  query GetPackTaskByFulfillment($fulfillmentId: ID!) {
    packTaskByFulfillment(fulfillmentId: $fulfillmentId) {
      id
      state
      fulfillmentId
      orderId
      assignedToId
      totalItems
      notes
      assignedAt
      startedAt
      completedAt
    }
  }
`;

export const GET_DELIVERY_TASK_BY_FULFILLMENT = gql`
  query GetDeliveryTaskByFulfillment($fulfillmentId: ID!) {
    deliveryTaskByFulfillment(fulfillmentId: $fulfillmentId) {
      id
      state
      fulfillmentId
      orderId
      assignedToId
      totalItems
      notes
      scheduledDate
      scheduledSlotStart
      scheduledSlotEnd
      failureReason
      assignedAt
      startedAt
      completedAt
    }
  }
`;

export const GET_AVAILABLE_DRIVERS = gql`
  query GetAvailableDrivers {
    availableDrivers {
      id
      firstName
      lastName
      emailAddress
    }
  }
`;

export const ASSIGN_DELIVERY_TASK_MUTATION = gql`
  mutation AssignDeliveryTask($deliveryTaskId: ID!, $driverId: ID!) {
    assignDeliveryTask(deliveryTaskId: $deliveryTaskId, driverId: $driverId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const START_DELIVERY_MUTATION = gql`
  mutation StartDelivery($deliveryTaskId: ID!) {
    startDelivery(deliveryTaskId: $deliveryTaskId) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const MARK_DELIVERED_MUTATION = gql`
  mutation MarkDelivered($deliveryTaskId: ID!, $notes: String) {
    markDelivered(deliveryTaskId: $deliveryTaskId, notes: $notes) {
      id
      state
      success
      errorCode
      message
    }
  }
`;

export const MARK_DELIVERY_FAILED_MUTATION = gql`
  mutation MarkDeliveryFailed($deliveryTaskId: ID!, $failureReason: String!) {
    markDeliveryFailed(deliveryTaskId: $deliveryTaskId, failureReason: $failureReason) {
      id
      state
      success
      errorCode
      message
    }
  }
`;
